import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { cancelMatch, getMyBookings } from "./api";
import { sanitizeApiMessage } from "./auth/clientAuth";

function toDateKey(value) {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatMatchDate(isoDate) {
  try {
    return new Date(isoDate).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return String(isoDate ?? "");
  }
}

function filterByDateAndLocation(rows, dateFilter, locationFilter) {
  const q = (locationFilter || "").trim().toLowerCase();
  return rows.filter((row) => {
    const dateHit = !dateFilter || toDateKey(row.date) === dateFilter;
    const locHit = !q || (row.location || "").toLowerCase().includes(q);
    return dateHit && locHit;
  });
}

function MyBookings() {
  const [joined, setJoined] = useState([]);
  const [hosted, setHosted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [banner, setBanner] = useState("");
  const [leaveBusyId, setLeaveBusyId] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");

  const loadBookings = async () => {
    setLoading(true);
    setError(null);
    try {
      const { response, data } = await getMyBookings();
      if (!response.ok) {
        const raw =
          data && typeof data === "object" && data.message
            ? data.message
            : `Could not load bookings (${response.status})`;
        throw new Error(sanitizeApiMessage(raw));
      }
      const j = data?.joined ?? data?.Joined;
      const h = data?.hosted ?? data?.Hosted;
      setJoined(Array.isArray(j) ? j : []);
      setHosted(Array.isArray(h) ? h : []);
    } catch (e) {
      setError(sanitizeApiMessage(e.message || "Failed to load your bookings."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadBookings();
  }, []);

  useEffect(() => {
    if (!banner) return undefined;
    const t = window.setTimeout(() => setBanner(""), 5200);
    return () => window.clearTimeout(t);
  }, [banner]);

  const filteredJoined = useMemo(
    () => filterByDateAndLocation(joined, dateFilter, locationFilter),
    [joined, dateFilter, locationFilter]
  );

  const filteredHosted = useMemo(
    () => filterByDateAndLocation(hosted, dateFilter, locationFilter),
    [hosted, dateFilter, locationFilter]
  );

  const handleLeave = async (matchId) => {
    if (!window.confirm("Leave this match? Your slot will be released.")) return;
    setLeaveBusyId(matchId);
    try {
      const { response, data } = await cancelMatch(matchId);
      if (!response.ok) {
        const msg = (data && data.message) || "Could not leave match.";
        setBanner(sanitizeApiMessage(String(msg)));
        return;
      }
      setBanner("You left the match.");
      await loadBookings();
    } catch {
      setBanner("Could not reach the server.");
    } finally {
      setLeaveBusyId(null);
    }
  };

  return (
    <main className="page">
      <section className="panel">
        <h1>My Matches / My Bookings</h1>

        <div className="filters my-bookings-filters" aria-label="Filter bookings and joinings">
          <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
          <input
            type="search"
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            placeholder="Filter by location"
            aria-label="Filter by location"
          />
        </div>

        {loading ? <p className="muted">Loading…</p> : null}
        {error ? <p className="error-text">{error}</p> : null}
        {banner ? <p className="success-text" role="status">{banner}</p> : null}

        <div className="booking-grid">
          <article className="list-card">
            <h3>Joined matches</h3>
            {!loading && !error && filteredJoined.length === 0 ? (
              <p className="muted">
                {joined.length === 0 && !dateFilter && !locationFilter.trim()
                  ? "You have no joined matches yet."
                  : "No joinings match these filters."}
              </p>
            ) : null}
            {filteredJoined.map((row) => (
              <div key={`${row.matchId}-${row.bookingId}`} className="booking-row">
                <strong>{row.sport}</strong>
                <span>{row.location}</span>
                <span className="muted">
                  {formatMatchDate(row.date)} · {row.matchTime}
                </span>
                {row.playerName ? (
                  <span className="muted small">Player: {row.playerName}</span>
                ) : null}
                <div className="booking-row__actions">
                  <Link to={`/matches?match=${row.matchId}`} className="ghost-btn small">
                    View match
                  </Link>
                  <button
                    type="button"
                    className="ghost-btn small"
                    disabled={leaveBusyId === row.matchId}
                    onClick={() => void handleLeave(row.matchId)}
                  >
                    {leaveBusyId === row.matchId ? "Leaving…" : "Leave match"}
                  </button>
                </div>
              </div>
            ))}
          </article>

          <article className="list-card">
            <h3>Hosted matches</h3>
            {!loading && !error && filteredHosted.length === 0 ? (
              <p className="muted">
                {hosted.length === 0 && !dateFilter && !locationFilter.trim()
                  ? "You are not hosting any matches yet."
                  : "No hosted matches match these filters."}
              </p>
            ) : null}
            {filteredHosted.map((row) => (
              <div key={row.matchId} className="booking-row">
                <strong>{row.sport}</strong>
                <span>{row.location}</span>
                <span className="muted">
                  {formatMatchDate(row.date)} · {row.matchTime}
                </span>
                <span className="muted small">
                  Slots: {row.slotsBooked ?? 0} booked, {row.slotsAvailable ?? 0} open
                </span>
                <div className="booking-row__actions">
                  <Link to={`/matches?match=${row.matchId}`} className="ghost-btn small">
                    Manage match
                  </Link>
                </div>
              </div>
            ))}
          </article>

          <article className="list-card">
            <h3>Tournaments</h3>
            <p className="muted">View and join tournaments from the tournaments page.</p>
            <Link to="/tournaments" className="ghost-btn small">
              Browse tournaments
            </Link>
          </article>
        </div>
      </section>
    </main>
  );
}

export default MyBookings;
